import { useI18n } from '@affine/i18n';
import { cssVarV2 } from '@streamlain/theme/v2';
import {
  createContext,
  type ReactNode,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import { SidebarSearchField } from '../sidebar-search-field';

const normalize = (value: string) =>
  value.normalize('NFKD').replace(/\p{M}/gu, '').toLocaleLowerCase();
export interface SidebarNavigationFilterState {
  active: boolean;
  animate: boolean;
  matches: (text: string) => boolean;
}
const noFilter: SidebarNavigationFilterState = {
  active: false,
  animate: true,
  matches: () => true,
};
const FilterContext = createContext<
  | (SidebarNavigationFilterState & {
      query: string;
      setQuery: (value: string) => void;
      placeholder: string;
    })
  | null
>(null);

/** Filter only the app's already accessible resources; never load document data. */
export function SidebarNavigationFilter({
  children,
  placeholder,
}: {
  children: ReactNode;
  placeholder: string;
}) {
  const [query, setQuery] = useState('');
  const tokens = useMemo(
    () => normalize(query).trim().split(/\s+/).filter(Boolean),
    [query]
  );
  const active = tokens.length > 0;
  const animate = useSidebarFilterAnimation(active);
  const value = useMemo(
    () => ({
      query,
      setQuery,
      placeholder,
      active,
      // Restore the original tree in one commit, without reopening each branch.
      animate,
      matches: (text: string) => {
        const normalizedText = normalize(text);
        return tokens.every(token => normalizedText.includes(token));
      },
    }),
    [active, animate, placeholder, query, tokens]
  );
  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
}

/** Restore the unfiltered tree before paint, without per-branch reopen motion. */
export function useSidebarFilterAnimation(active: boolean): boolean {
  const [committedActive, setCommittedActive] = useState(active);
  useLayoutEffect(() => setCommittedActive(active), [active]);
  return !active && !committedActive;
}

export function useSidebarNavigationFilter(): SidebarNavigationFilterState {
  return useContext(FilterContext) ?? noFilter;
}

export function SidebarNavigationFilterField() {
  const filter = useContext(FilterContext);
  if (!filter) return null;
  return (
    <SidebarSearchField
      railAligned
      value={filter.query}
      onChange={filter.setQuery}
      placeholder={filter.placeholder}
      ariaLabel={filter.placeholder}
    />
  );
}

export function SidebarNavigationFilterEmpty() {
  const t = useI18n();
  return (
    <p
      role="status"
      style={{
        margin: '12px 18px',
        fontSize: 13,
        color: cssVarV2('text/secondary'),
      }}
    >
      {t['com.streamlain.cmdk.no-results']()}
    </p>
  );
}
